// Pure stage-name helpers shared by the backend runtime and the infra (Pulumi)
// program. Kept dependency-free so infra-v3 can import it directly — its tsconfig
// `rootDir` forbids reaching into backend/, but @gap/utils is a package (resolved
// the same way region-utils is, from both sides).

/**
 * Canonical (v2) stage name for a possibly-v3 stage. The v3 prod-like and dryrun
 * cutover stages carry a `-v3` suffix; their canonical sibling is the bare v2 stage.
 * Used wherever v3 must SHARE v2's namespace rather than isolate from it:
 *   - SSM secret paths — `backend/.../SecretService` builds the parameter PATH from
 *     this, and the infra IAM grants (`ApiStack`, `XpmSyncQueueStack`, `CronStack`,
 *     `JobsStack`) build the parameter ARN from it. They MUST agree or the Lambda
 *     gets AccessDenied at runtime (NO-416). This single source guarantees they do.
 *   - The FYI environment select (`backend/.../FYIService`).
 * Non-cutover stages (feature/bug/rc/dev/preprod/prod) are returned unchanged.
 */
export function canonicalStage(stage: string): string
export function canonicalStage(stage: string | undefined): string | undefined
export function canonicalStage(stage: string | undefined): string | undefined {
   if (stage === 'preprod-v3') return 'preprod'
   if (stage === 'prod-v3') return 'prod'
   if (stage === 'dryrun-v3') return 'dryrun'
   return stage
}

/**
 * True for the prod-like stages — prod and preprod, plus their `-v3` cutover siblings.
 * Runtime counterpart of infra's isProdLikeStage; both reduce to canonicalStage ∈ {prod, preprod}.
 */
export function isProdLikeStage(stage: string | undefined): boolean {
   const canonical = canonicalStage(stage)
   return canonical === 'prod' || canonical === 'preprod'
}

/**
 * Sanitizes a stage name for use as a PostgreSQL database/user name.
 * PostgreSQL identifiers can't have hyphens.
 */
export function sanitizeStageNameForPostgresDb(stage: string): string {
   return stage.replace(/-/g, '_')
}

/**
 * True for the dev-account tiers where dev-only tooling (Dev Sandbox, the dev-mode tRPC guard)
 * is allowed: the dev/preview/feature/bug branch stages and the dryrun rehearsal tier
 * (`startsWith('dryrun')` covers both dryrun and dryrun-v3).
 */
export function isDevStage(stage: string | undefined): boolean {
   if (!stage) return false
   return (
      stage.startsWith('dev') ||
      stage.startsWith('preview-') ||
      stage.startsWith('feature-') ||
      stage.startsWith('bug-') ||
      stage.startsWith('dryrun')
   )
}
